import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BRANDS } from "@/data/brands";
import { useT } from "@/i18n/LanguageContext";

// Mobile expanded navigation drawer. Rendered under the header when the
// hamburger is tapped; contains brand shortcuts, section links, primary CTA,
// and a language switcher grid.
export default function MobileMenu({ open, links, onSectionClick, onBrandClick, onCtaClick, onSampleClick }) {
  const { t, lang, setLang, locales } = useT();
  const visibleBrands = useMemo(
    () => BRANDS.filter((b) => !b.hideFromNav),
    []
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          className="lg:hidden bg-white border-b border-black/5 px-6 pb-6"
        >
          <div className="flex flex-col divide-y divide-black/10">
            <button
              onClick={() => onSectionClick("brands")}
              className="py-4 text-left text-lg font-medium"
            >
              {t("nav.brands")}
            </button>
            <div className="pl-4 py-2 flex flex-col gap-3">
              {visibleBrands.map((b) => (
                <button
                  key={b.slug}
                  onClick={() => onBrandClick(b.slug)}
                  aria-label={b.name}
                  className="flex items-center h-24"
                >
                  <img
                    src={b.logo}
                    alt={b.name}
                    className={`${b.logoClass || "h-12 max-w-[220px]"} w-auto object-contain`}
                  />
                </button>
              ))}
            </div>
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={l.testId + "-mobile"}
                onClick={() => onSectionClick(l.id)}
                className="py-4 text-left text-lg font-medium"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={onCtaClick}
              className="mt-3 py-3 rounded-full bg-[#001F39] text-white font-medium"
            >
              {t("nav.cta")}
            </button>
            {onSampleClick && (
              <button
                data-testid="header-sample-request-mobile"
                onClick={onSampleClick}
                className="mt-2 py-3 rounded-full border border-black/15 text-black font-medium"
              >
                {t("nav.sample") !== "nav.sample" ? t("nav.sample") : "Request Samples"}
              </button>
            )}
            <div className="pt-3 grid grid-cols-3 gap-2">
              {locales.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-option-mobile-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-2 rounded-full text-xs font-medium border ${
                    l.code === lang
                      ? "bg-[#25bfcf] text-white border-[#25bfcf]"
                      : "bg-white text-neutral-800 border-black/15"
                  }`}
                >
                  {l.flag} {l.native}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
